import { useCallback, useEffect, useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { api } from '../api/client.ts';
import { MeProvider, useMe } from './me.tsx';
import { ContextRow, MenuBar } from './MenuBar.tsx';
import type { MachineSummary } from './MenuBar.tsx';
import { pathFor, readPath, useRoute } from './router.tsx';
import { CouldNotLoad, NoPermission, ViewerBanner } from '../components/states.tsx';
import { Card, Page } from '../components/basics.tsx';
import { SignIn } from '../screens/SignIn.tsx';
import { Projects } from '../screens/Projects.tsx';
import { Board } from '../screens/board/Board.tsx';
import { TakeDetail } from '../screens/TakeDetail.tsx';
import { Runs } from '../screens/Runs.tsx';
import { Inbox } from '../screens/Inbox.tsx';
import { Help } from '../screens/Help.tsx';
import { Vcs } from '../screens/Vcs.tsx';
import { Capture } from '../screens/Capture.tsx';
import { Search } from '../screens/Search.tsx';
import { FinalMovie } from '../screens/FinalMovie.tsx';
import { Edits } from '../screens/Edits.tsx';
import { Pipeline } from '../screens/Pipeline.tsx';
import { ShareLinks } from '../screens/ShareLinks.tsx';
import { Admin } from '../screens/Admin.tsx';
import { TimelineScreen } from '../screens/Timeline.tsx';

/** The screens that exist so far. The rest arrive as they are built. */
/**
 * Every screen is built. The set stays so an unknown path says so plainly
 * rather than drawing an empty page.
 */
const BUILT = new Set([
  'projects',
  'board',
  'take',
  'runs',
  'inbox',
  'help',
  'vcs',
  'capture',
  'search',
  'final_movie',
  'edits',
  'pipeline',
  'share_links',
  'admin',
  'timeline'
]);

export function App() {
  return (
    <MeProvider>
      <Shell />
    </MeProvider>
  );
}

function Shell() {
  const state = useMe();
  const { path, go } = useRoute();
  const where = readPath(path);

  // The share page is deliberately outside everything: rule 13 says the
  // outside page has no route into the rest of Maestro, so it does not get the
  // menu bar, and it does not need a session.
  if (where.screen === 'share') return <SharePage token={where.id!} />;

  if (state.status === 'signed_out') {
    return <SignIn onSignedIn={() => state.reload()} />;
  }
  if (state.status === 'failed') {
    return (
      <div style={{ padding: 'clamp(16px, 4vw, 64px)' }}>
        <CouldNotLoad what="Maestro" error={state.error} onRetry={state.reload} />
      </div>
    );
  }

  return <SignedIn where={where} path={path} go={go} />;
}

function SignedIn({
  where,
  go
}: {
  where: ReturnType<typeof readPath>;
  path: string;
  go: (to: string) => void;
}) {
  const state = useMe();
  const answer = state.status === 'ready' ? state.answer : null;
  const [projects, setProjects] = useState<{ project_id: string; name: string }[]>([]);
  const [inbox, setInbox] = useState<number | null>(null);
  const [machines, setMachines] = useState<MachineSummary | null>(null);

  // The bar's own data: which projects there are, how many machines are on,
  // and how much needs a person. Asked once here rather than by every screen.
  useEffect(() => {
    void api
      .get<{ projects: { project_id: string; name: string }[] }>('/api/projects')
      .then((body) => setProjects(body.projects))
      .catch(() => setProjects([]));
    void api
      .get<{ machines: { host: string; online: boolean; retired?: boolean; queued: number }[] }>(
        '/api/machines'
      )
      .then((body) =>
        setMachines({
          on: body.machines.filter((m) => m.online).length,
          total: body.machines.filter((m) => !m.retired).length,
          flagged: body.machines
            .filter((m) => !m.online && !m.retired && m.queued > 0)
            .map((m) => ({ host: m.host, queued: m.queued }))
        })
      )
      .catch(() => setMachines(null));
  }, []);

  useEffect(() => {
    if (!where.projectId) {
      setInbox(null);
      return;
    }
    void api
      .get<{ total: number }>(`/api/inbox?project_id=${encodeURIComponent(where.projectId)}`)
      .then((body) => setInbox(body.total))
      .catch(() => setInbox(null));
  }, [where.projectId]);

  const onSearch = useCallback(() => {
    if (where.projectId) go(pathFor(where.projectId, 'search'));
  }, [where.projectId, go]);

  // ⌘K opens search, as the bar advertises.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSearch]);

  const project = projects.find((p) => p.project_id === where.projectId);
  const crumbs = [
    { label: 'Projects' },
    ...(project ? [{ label: project.name }] : where.projectId ? [{ label: where.projectId }] : []),
    ...(where.screen !== 'projects'
      ? [{ label: answer?.menu.tabs.concat(answer.menu.more).find((t) => t.key === where.screen)?.says ?? screenName(where.screen) }]
      : [])
  ];

  return (
    <>
      <MenuBar
        projectId={where.projectId}
        projects={projects}
        screen={where.screen}
        onGo={go}
        inboxCount={inbox}
        machines={machines}
        onSearch={onSearch}
      />
      <ContextRow crumbs={crumbs} instead={answer?.instead_of_action ?? null} />
      <main>
        {answer?.me.role === 'viewer' && (
          <div style={{ padding: `${space.control}px clamp(16px, 4vw, 64px) 0` }}>
            <ViewerBanner />
          </div>
        )}
        <Screen where={where} go={go} />
      </main>
    </>
  );
}

function screenName(key: string): string {
  return key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Screens that are about Maestro itself and need no project. */
const GLOBAL_SCREENS = new Set(['admin', 'help']);

function Screen({
  where,
  go
}: {
  where: ReturnType<typeof readPath>;
  go: (to: string) => void;
}) {
  const state = useMe();
  const answer = state.status === 'ready' ? state.answer : null;

  // Admin and Help are about Maestro, not about one project — storage roots,
  // connections, people, machines, tools, processes. Requiring a project to
  // reach them meant a fresh deployment, which has none, could not reach
  // Admin settings at all: the menu item quietly bounced to the project list.
  if (!where.projectId && GLOBAL_SCREENS.has(where.screen)) {
    return where.screen === 'admin' ? <Admin /> : <Help />;
  }

  if (where.screen === 'projects' || !where.projectId) {
    return (
      <Projects
        onOpen={(projectId) => go(pathFor(projectId, 'board'))}
        // `/projects/new` is where the New project menu item goes, so the
        // dialog is a route rather than a piece of hidden state.
        making={where.id === 'new'}
        onOpenMaking={() => go('/projects/new')}
        onCloseMaking={() => go('/projects')}
      />
    );
  }

  // A screen this role cannot reach: say so rather than drawing an empty one.
  const item = answer?.menu.tabs.concat(answer.menu.more).find((t) => t.key === where.screen);
  if (answer && !item && !BUILT.has(where.screen)) {
    return (
      <div style={{ padding: 'clamp(16px, 4vw, 64px)' }}>
        <NoPermission what={screenName(where.screen)} role={answer.me.role} />
      </div>
    );
  }

  if (!BUILT.has(where.screen)) {
    return (
      <Page
        eyebrow={`MAESTRO · ${screenName(where.screen).toUpperCase()}`}
        title={item?.says ?? screenName(where.screen)}
        description="This screen is not built yet. Its API is, and the next pass wires it up."
      >
        <Card>
          <span style={{ ...text.small, color: colour.inkSoft }}>
            The server already answers for this screen — see the API table in the README. Nothing
            here is blocked on a decision; it is just the next piece of work.
          </span>
        </Card>
      </Page>
    );
  }

  const projectId = where.projectId;
  switch (where.screen) {
    case 'board':
      return <Board projectId={projectId} onOpenPass={(passId) => go(pathFor(projectId, 'pass', passId))} />;
    case 'take':
      return <TakeDetail takeId={where.id!} />;
    case 'runs':
      return <Runs projectId={projectId} />;
    case 'inbox':
      return <Inbox projectId={projectId} onGo={go} />;
    case 'help':
      return <Help />;
    case 'vcs':
      return <Vcs projectId={projectId} />;
    case 'capture':
      return <Capture projectId={projectId} takeId={where.id} />;
    case 'final_movie':
      return <FinalMovie projectId={projectId} />;
    case 'edits':
      return <Edits projectId={projectId} />;
    case 'pipeline':
      return <Pipeline projectId={projectId} />;
    case 'share_links':
      return <ShareLinks projectId={projectId} />;
    case 'admin':
      return <Admin />;
    case 'timeline':
      // `/p/:id/timeline/<target>:<id>`, so one route serves a take, a pass or
      // a splat without three near-identical screens.
      return (
        <TimelineScreen
          target={(where.id ?? 'take:').split(':')[0]!}
          id={(where.id ?? '').split(':')[1] ?? ''}
          projectId={projectId}
          onOpen={(target, id) => go(pathFor(projectId, 'timeline', `${target}:${id}`))}
        />
      );
    case 'search':
      return (
        <Search
          projectId={projectId}
          onOpenBoard={() => go(pathFor(projectId, 'board'))}
        />
      );
    default:
      return <Projects onOpen={(id) => go(pathFor(id, 'board'))} />;
  }
}

/**
 * The outside page. No menu bar, no sign-in, and no link anywhere else
 * (rule 13). An expired or turned-off link gets the one sentence and nothing
 * else.
 */
function SharePage({ token }: { token: string }) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'gone' } | { status: 'ready'; view: unknown }
  >({ status: 'loading' });

  useEffect(() => {
    void api
      .get<unknown>(`/share/${encodeURIComponent(token)}`)
      .then((view) => setState({ status: 'ready', view }))
      .catch(() => setState({ status: 'gone' }));
  }, [token]);

  if (state.status === 'loading') {
    return <Unavailable says="Opening…" />;
  }
  if (state.status === 'gone') {
    return <Unavailable says="This link is no longer available." />;
  }
  return (
    <div style={{ padding: 'clamp(16px, 4vw, 64px)', display: 'flex', flexDirection: 'column', gap: space.section }}>
      <span style={{ ...text.eyebrow, color: colour.inkMuted }}>SHARED FROM MAESTRO · READ ONLY</span>
      <Card>
        <pre style={{ ...text.mono, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {JSON.stringify(state.view, null, 2)}
        </pre>
      </Card>
    </div>
  );
}

function Unavailable({ says }: { says: string }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
      <Card style={{ maxWidth: 420, textAlign: 'center' }}>
        <span style={{ ...text.body, color: colour.inkSoft }}>{says}</span>
      </Card>
    </div>
  );
}
